// Email and Notification API module
import { BaseApiClient } from './BaseApiClient';
import type { AxiosProgressEvent } from 'axios';
import type {
  AdminEmailRecord,
  AdminEmailRecordListResponse,
} from '@/types/emailRecords';
import type {
  EmailCampaign,
  EmailCampaignCreateRequest,
  EmailCampaignUpdateRequest,
  EmailCampaignScheduleRequest,
  EmailCampaignListResponse,
  EmailCampaignStats,
  EmailCampaignStatus,
} from '@/types/emailCampaign';

export class EmailNotificationApi extends BaseApiClient {
  constructor() {
    super('EmailNotificationAPI');
  }

  /**
   * Send preview email
   */
  async previewEmail(data: Record<string, unknown>): Promise<{ html: string }> {
    try {
      const response = await this.api.post<{ html: string }>('/email/preview', data);
      return response.data;
    } catch (error) {
      this.logger.error('Email preview failed:', error);
      throw error;
    }
  }

  /**
   * Upload an image for use in admin-composed emails.
   * Returns a public URL that can be used in <img src="...">.
   */
  async uploadEmailImage(
    file: File,
    options?: { onProgress?: (percent: number) => void }
  ): Promise<{
    url: string;
    filename: string;
    content_type: string;
    size_bytes: number;
  }> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await this.api.post('/email/upload-image', formData, {
        // Override the default JSON content type for this request
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (evt: AxiosProgressEvent) => {
          if (!options?.onProgress) return;
          const total = evt.total ?? file.size;
          if (!total) return;
          const percent = Math.max(0, Math.min(100, Math.round((evt.loaded / total) * 100)));
          options.onProgress(percent);
        },
      });
      return response.data;
    } catch (error) {
      this.logger.error('Email image upload failed:', error);
      throw error;
    }
  }

  /**
   * Email Notifications History (Ops /emails)
   */
  async getEmailRecords(params?: {
    start_date?: string;
    end_date?: string;
    search?: string;
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<AdminEmailRecordListResponse> {
    const response = await this.api.get<AdminEmailRecordListResponse>('/email/records', { params });
    return response.data;
  }

  async getEmailRecord(recordId: string): Promise<AdminEmailRecord> {
    const response = await this.api.get<AdminEmailRecord>(`/email/records/${recordId}`);
    return response.data;
  }

  /**
   * Cancel an in-progress email send (by record id)
   */
  async cancelEmailRecord(recordId: string): Promise<AdminEmailRecord> {
    const response = await this.api.post<AdminEmailRecord>(`/email/records/${recordId}/cancel`);
    return response.data;
  }

  // Email Campaign Management API
  async getEmailCampaigns(
    filters?: { status?: EmailCampaignStatus; search?: string; page?: number; limit?: number }
  ): Promise<EmailCampaignListResponse> {
    const response = await this.api.get<EmailCampaignListResponse>('/email/campaigns', { params: filters });
    return response.data;
  }

  async getEmailCampaign(campaignId: string): Promise<EmailCampaign> {
    const response = await this.api.get<EmailCampaign>(`/email/campaigns/${campaignId}`);
    return response.data;
  }

  async createEmailCampaign(campaign: EmailCampaignCreateRequest): Promise<EmailCampaign> {
    const response = await this.api.post<EmailCampaign>('/email/campaigns', campaign);
    return response.data;
  }

  async updateEmailCampaign(campaignId: string, updates: EmailCampaignUpdateRequest): Promise<EmailCampaign> {
    const response = await this.api.put<EmailCampaign>(`/email/campaigns/${campaignId}`, updates);
    return response.data;
  }

  async previewEmailCampaign(campaignId: string, language?: string): Promise<{ html: string }> {
    const params = language ? { language } : {};
    const response = await this.api.post<{ html: string }>(`/email/campaigns/${campaignId}/preview`, {}, { params });
    return response.data;
  }

  async scheduleEmailCampaign(campaignId: string, scheduleData: EmailCampaignScheduleRequest): Promise<EmailCampaign> {
    const response = await this.api.post<EmailCampaign>(`/email/campaigns/${campaignId}/schedule`, scheduleData);
    return response.data;
  }

  async sendEmailCampaign(campaignId: string): Promise<{ success: boolean; message: string; campaign_id: string }> {
    const response = await this.api.post<{ success: boolean; message: string; campaign_id: string }>(`/email/campaigns/${campaignId}/send`);
    return response.data;
  }

  async cancelEmailCampaign(campaignId: string): Promise<EmailCampaign> {
    const response = await this.api.post<EmailCampaign>(`/email/campaigns/${campaignId}/cancel`);
    return response.data;
  }

  async unscheduleEmailCampaign(campaignId: string): Promise<EmailCampaign> {
    const response = await this.api.post<EmailCampaign>(`/email/campaigns/${campaignId}/unschedule`);
    return response.data;
  }

  async resumeEmailCampaign(campaignId: string): Promise<{ success: boolean; message: string; campaign_id: string; sent_count: number; recipient_count: number; remaining: number }> {
    const response = await this.api.post<{ success: boolean; message: string; campaign_id: string; sent_count: number; recipient_count: number; remaining: number }>(`/email/campaigns/${campaignId}/resume`);
    return response.data;
  }

  async getEmailCampaignStats(campaignId: string): Promise<EmailCampaignStats> {
    const response = await this.api.get<EmailCampaignStats>(`/email/campaigns/${campaignId}/stats`);
    return response.data;
  }

  async translateText(data: {
    text: string;
    html_content: string;
    source_language?: string;  // Optional, defaults to 'en' on backend
    target_language: string;
  }): Promise<{
    translated_text: string;
    translated_html: string;
  }> {
    const response = await this.api.post<{
      translated_text: string;
      translated_html: string;
    }>('/email/translate', data);
    return response.data;
  }

  async improveEmailContent(data: {
    subject: string;
    html_content: string;
    language?: string;
  }): Promise<{
    subject: string;
    html_content: string;
  }> {
    const response = await this.api.post<{
      subject: string;
      html_content: string;
    }>('/email/campaigns/improve-content', data);
    return response.data;
  }

  async deleteEmailCampaign(campaignId: string): Promise<{ success: boolean; message: string }> {
    const response = await this.api.delete<{ success: boolean; message: string }>(`/email/campaigns/${campaignId}`);
    return response.data;
  }
}

